import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, DollarSign, Calendar as CalIcon, FileWarning, AlertTriangle } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { DbEvent } from '../types';
import { calcBalance, fmtMoney, isOverdue } from '../lib/pricing';
import { Calendar } from '../components/Calendar';

interface Metrics {
  totalOwed: number;
  upcomingThisMonth: number;
  unsignedContracts: number;
  overdueCount: number;
}

function MetricCard({
  label, value, icon: Icon, alert,
}: {
  label: string; value: string | number; icon: React.ElementType; alert?: boolean;
}) {
  return (
    <div className={`bg-white rounded-xl border p-4 flex items-center gap-4 ${alert ? 'border-red-200 bg-red-50' : 'border-gray-200'}`}>
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${alert ? 'bg-red-100' : 'bg-gold-50'}`}>
        <Icon size={20} className={alert ? 'text-red-500' : 'text-gold-600'} />
      </div>
      <div>
        <p className="text-xs text-gray-500 font-medium">{label}</p>
        <p className={`text-xl font-bold ${alert ? 'text-red-600' : 'text-gray-900'}`}>{value}</p>
      </div>
    </div>
  );
}

export function Dashboard() {
  const navigate = useNavigate();
  const [events, setEvents] = useState<DbEvent[]>([]);
  const [metrics, setMetrics] = useState<Metrics>({ totalOwed: 0, upcomingThisMonth: 0, unsignedContracts: 0, overdueCount: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const monthStart = `${year}-${month}-01`;
      const lastDay = new Date(year, now.getMonth() + 1, 0).getDate();
      const monthEnd = `${year}-${month}-${String(lastDay).padStart(2, '0')}`;

      const [eventsRes, contractsRes] = await Promise.all([
        supabase.from('events').select('*, clients(*)').neq('status', 'cancelled'),
        supabase.from('contracts').select('contract_status'),
      ]);

      const allEvents: DbEvent[] = eventsRes.data ?? [];
      setEvents(allEvents);

      const totalOwed = allEvents.reduce((sum, e) => sum + Math.max(0, calcBalance(e)), 0);
      const upcomingThisMonth = allEvents.filter(
        e => e.status === 'upcoming' && e.event_date >= monthStart && e.event_date <= monthEnd
      ).length;
      const unsignedContracts = (contractsRes.data ?? []).filter(c => c.contract_status !== 'signed').length;
      const overdueCount = allEvents.filter(isOverdue).length;

      setMetrics({ totalOwed, upcomingThisMonth, unsignedContracts, overdueCount });
      setLoading(false);
    }
    load();
  }, []);

  const upcoming = events
    .filter(e => e.status === 'upcoming')
    .sort((a, b) => a.event_date.localeCompare(b.event_date))
    .slice(0, 10);

  const statusBadge = (status: string) => {
    const map: Record<string, string> = {
      upcoming: 'bg-blue-100 text-blue-700',
      completed: 'bg-green-100 text-green-700',
      cancelled: 'bg-gray-100 text-gray-500',
    };
    return map[status] ?? 'bg-gray-100 text-gray-500';
  };

  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const fmtEventDate = (d: string) => {
    const [y, m, day] = d.split('-');
    return `${months[parseInt(m)-1]} ${parseInt(day)}, ${y}`;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-24">
        <div className="w-8 h-8 border-4 border-gold-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-6 space-y-6">
      {/* Metrics */}
      <div className="grid grid-cols-2 gap-3">
        <MetricCard label="Total Owed" value={fmtMoney(metrics.totalOwed)} icon={DollarSign} />
        <MetricCard label="Events This Month" value={metrics.upcomingThisMonth} icon={CalIcon} />
        <MetricCard
          label="Unsigned Contracts"
          value={metrics.unsignedContracts}
          icon={FileWarning}
          alert={metrics.unsignedContracts > 0}
        />
        <MetricCard
          label="Overdue Balances"
          value={metrics.overdueCount}
          icon={AlertTriangle}
          alert={metrics.overdueCount > 0}
        />
      </div>

      {/* Calendar */}
      <div>
        <h2 className="font-serif text-lg font-semibold text-gray-900 mb-3">Calendar</h2>
        <Calendar
          events={events}
          onEventClick={ev => navigate(`/events/${ev.id}`)}
        />
      </div>

      {/* Upcoming events */}
      <div>
        <h2 className="font-serif text-lg font-semibold text-gray-900 mb-3">Upcoming Events</h2>
        {upcoming.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 px-4 py-8 text-center text-gray-400 text-sm">
            No upcoming events
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-gray-200 divide-y divide-gray-100 overflow-hidden">
            {upcoming.map(ev => {
              const balance = calcBalance(ev);
              const overdue = isOverdue(ev);
              return (
                <button
                  key={ev.id}
                  onClick={() => navigate(`/events/${ev.id}`)}
                  className="w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-medium text-gray-900 truncate">{ev.clients?.name ?? '—'}</p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {ev.event_type ?? 'Event'} · {fmtEventDate(ev.event_date)}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className={`text-sm font-semibold ${overdue ? 'text-red-600' : balance > 0 ? 'text-gray-900' : 'text-green-600'}`}>
                        {fmtMoney(balance)}
                      </span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusBadge(ev.status)}`}>
                        {ev.status}
                      </span>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* FAB */}
      <button
        onClick={() => navigate('/bookings/new')}
        className="fixed bottom-20 right-5 md:bottom-8 md:right-8 w-14 h-14 bg-gold-500 hover:bg-gold-600 text-white rounded-full shadow-lg flex items-center justify-center transition-colors z-20"
        aria-label="New booking"
      >
        <Plus size={28} />
      </button>
    </div>
  );
}
